/**
 * Medien-Renderer (Bild/Video/Audio/Embed) mit Vergrößern-Overlay (1:1 zur
 * Web-App, media.js + app.js): Audio als <audio>, Video/Audio senden
 * played/progress(throttled)/paused/completed; Overlay übernimmt currentTime,
 * schließt per Esc/Backdrop. v1-Kapitel haben noch keine Medien — Gerüst.
 */
import { useEffect, useRef, useState } from "preact/hooks";
import type { Block } from "../data/types";
import { sendProgressEvent } from "../state/progress";

interface Ctx {
  courseId: string;
  moduleId: string;
  blockId: string;
}

export function MediaFigure({ block, ctx }: { block: Block; ctx?: Ctx }) {
  const [open, setOpen] = useState(false);
  const mediaRef = useRef<HTMLVideoElement | HTMLAudioElement>(null);
  const b = block as Block & { src?: string; title?: string; caption?: string; poster?: string };

  const isVideo = block.type === "video";
  const isAudio = block.type === "audio";
  const isEmbed = block.type === "embed";
  const caption = b.caption || b.title;
  const itemId = b.src || block.id;
  const itemType = isAudio ? "audio" : "video";

  // Fortschritts-Events für Video/Audio (1:1 zum Web-Vertrag).
  useEffect(() => {
    const el = mediaRef.current;
    if (!el || isEmbed || (!isVideo && !isAudio) || !ctx) return;
    let lastSent = 0;
    const emit = (eventType: string) => {
      const pos = Number(el.currentTime || 0);
      const dur = Number(el.duration || 0);
      if (eventType === "progress" && Math.abs(pos - lastSent) < 15) return;
      lastSent = pos;
      sendProgressEvent({
        courseId: ctx.courseId,
        moduleId: ctx.moduleId,
        blockId: ctx.blockId,
        itemId,
        itemType,
        eventType,
        positionSeconds: pos,
        durationSeconds: isFinite(dur) ? dur : null,
        completed: eventType === "completed",
      });
    };
    const onPlay = () => emit("played");
    const onTime = () => emit("progress");
    const onPause = () => emit("paused");
    const onEnded = () => emit("completed");
    el.addEventListener("play", onPlay);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnded);
    return () => {
      el.removeEventListener("play", onPlay);
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnded);
    };
  }, [itemId]);

  // Overlay: Esc schließt; currentTime aus dem Inline-Element übernehmen.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!b.src) return null;

  const startAt = mediaRef.current ? mediaRef.current.currentTime : 0;

  return (
    <figure class={`lesson-media lesson-media-${isVideo || isEmbed ? "video" : "image"}`}>
      {(isVideo || isEmbed) && (
        <button type="button" class="media-expand-btn" aria-label="Größer anzeigen" onClick={() => setOpen(true)}>
          ↗
        </button>
      )}
      {isEmbed ? (
        <div class="video-frame">
          <iframe src={b.src} title={b.title || "Video"} allow="autoplay; fullscreen; picture-in-picture; encrypted-media" allowFullScreen />
        </div>
      ) : isVideo ? (
        <video ref={mediaRef as any} controls preload="metadata" poster={b.poster} src={b.src} />
      ) : isAudio ? (
        <audio ref={mediaRef as any} controls preload="metadata" src={b.src} style="width:100%" />
      ) : (
        <img src={b.src} alt={caption || ""} loading="lazy" />
      )}
      {caption ? <figcaption>{caption}</figcaption> : null}

      {open ? (
        <div class="media-overlay" onClick={() => setOpen(false)}>
          <section class="media-overlay-panel glass" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <button type="button" class="media-overlay-close" aria-label="Schließen" onClick={() => setOpen(false)}>
              ×
            </button>
            {isEmbed ? (
              <iframe src={b.src} title={b.title || "Video"} allow="autoplay; fullscreen; picture-in-picture; encrypted-media" allowFullScreen />
            ) : (
              <video
                controls
                autoplay
                preload="metadata"
                poster={b.poster}
                src={b.src}
                ref={(el) => {
                  if (el && startAt) {
                    el.addEventListener("loadedmetadata", () => {
                      try {
                        el.currentTime = startAt;
                      } catch {
                        /* ignore */
                      }
                    }, { once: true });
                  }
                }}
              />
            )}
          </section>
        </div>
      ) : null}
    </figure>
  );
}
